import React, { useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Languages,
  Mic,
  Hand,
  Palette,
  HelpCircle,
  Database,
  Shield,
  User,
  Download,
  Trash2,
  Check,
  Volume2,
  ScanFace,
  Bell,
  EyeOff,
  RotateCcw,
  MessageSquare,
  Loader2,
  LogOut,
  Sparkles,
  Lightbulb,
} from "lucide-react";
import { supabase, signOut, BYPASS_AUTH } from "../services/supabase";
import { getSettings, setSetting, applyTheme } from "../services/settings";
import { getAllMemories, deleteAllMemories } from "../services/memories";
import { getProfile, upsertProfile } from "../services/profile";
import { useT } from "../services/i18n";

const LANGS = [
  { value: "fr", label: "Français" },
  { value: "en", label: "English" },
  { value: "ru", label: "Русский" },
];

const SOUND_SUB = {
  fr: "À l'enregistrement d'un souvenir.",
  en: "When you save a memory.",
  ru: "При сохранении воспоминания.",
};

export default function SettingsScreen({ onBack }) {
  const { t, lang } = useT();
  const [view, setView] = useState("main");
  const [s, setS] = useState(getSettings());
  const [userEmail, setUserEmail] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUserEmail(data?.user?.email || ""));
  }, []);

  const update = (key, value) => {
    const next = setSetting(key, value);
    setS({ ...next });
    if (key === "theme") applyTheme(value);
    if (key === "uiLang") window.dispatchEvent(new Event("lang-change"));
  };

  const handleExport = async () => {
    setBusy("export");
    setNotice("");
    try {
      const data = await getAllMemories();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rememor-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setNotice(t("set_exported").replace("{n}", data.length));
    } catch (e) {
      setNotice(t("set_err_export"));
    } finally {
      setBusy("");
    }
  };

  const handleDelete = async () => {
    setBusy("delete");
    try {
      await deleteAllMemories();
      setConfirmDelete(false);
      setNotice(t("set_deleted_all"));
    } catch (e) {
      setNotice(t("set_err_delete_all"));
    } finally {
      setBusy("");
    }
  };

  const goBack = () => (view === "main" ? onBack() : setView("main"));
  const title =
    view === "instructions"
      ? t("set_how_title")
      : view === "privacy"
        ? t("set_privacy_title")
        : t("set_title");

  return (
    <div className="min-h-[100dvh]" data-testid="settings-screen">
      <header className="flex items-center gap-2 px-4 pt-[max(20px,env(safe-area-inset-top))] pb-4">
        <button
          data-testid="settings-back-btn"
          onClick={goBack}
          className="grid h-10 w-10 place-items-center rounded-full text-[var(--rmr-text-2)] transition-colors hover:bg-[var(--rmr-surface)] hover:text-[var(--rmr-accent)]"
          aria-label={t("set_back_aria")}
        >
          <ChevronLeft size={24} />
        </button>
        <h1 className="brand text-2xl font-bold text-[var(--rmr-text)]">{title}</h1>
      </header>

      <div className="space-y-3 px-4 pb-[calc(100px+env(safe-area-inset-bottom))]">
        {view === "instructions" && <Instructions t={t} />}
        {view === "privacy" && <Privacy t={t} onData={() => setView("main")} />}

        {view === "main" && (
          <>
            {notice && (
              <div
                className="rounded-2xl bg-[var(--rmr-chip)] px-4 py-2 text-center text-sm text-[var(--rmr-accent)]"
                data-testid="settings-notice"
              >
                {notice}
              </div>
            )}

            <Card>
              <SettingRow icon={<Languages size={20} />} title={t("set_ui_lang")} subtitle={t("set_ui_lang_sub")}>
                <Select testid="select-ui-lang" value={s.uiLang} onChange={(v) => update("uiLang", v)} options={LANGS} />
              </SettingRow>
              <Divider />
              <SettingRow icon={<Mic size={20} />} title={t("set_input_lang")} subtitle={t("set_input_lang_sub")}>
                <Select testid="select-input-lang" value={s.inputLang} onChange={(v) => update("inputLang", v)} options={LANGS} />
              </SettingRow>
            </Card>

            <Card>
              <SettingRow icon={<Hand size={20} />} title={t("set_hand")} subtitle={t("set_hand_sub")} />
              <div className="mt-3">
                <Segmented
                  testid="segment-hand"
                  value={s.hand}
                  onChange={(v) => update("hand", v)}
                  options={[
                    { value: "left", label: t("set_left") },
                    { value: "right", label: t("set_right") },
                  ]}
                />
              </div>
            </Card>

            <Card>
              <SettingRow icon={<Palette size={20} />} title={t("set_theme")} subtitle={t("set_theme_sub")} />
              <div className="mt-3">
                <Segmented
                  testid="segment-theme"
                  value={s.theme}
                  onChange={(v) => update("theme", v)}
                  options={[
                    { value: "light", label: t("set_day") },
                    { value: "dark", label: t("set_night") },
                    { value: "system", label: t("set_system") },
                  ]}
                />
              </div>
            </Card>

            <Card>
              <SettingRow
                icon={<Volume2 size={20} />}
                title={t("set_sound_vib")}
                subtitle={SOUND_SUB[lang] || SOUND_SUB.fr}
              >
                <Toggle
                  testid="setting-sound-toggle"
                  checked={!!s.sound}
                  onChange={() => update("sound", !s.sound)}
                />
              </SettingRow>
            </Card>

            <Card>
              <SettingRow
                icon={<Sparkles size={20} />}
                title={t("set_embellish")}
                subtitle={t("set_embellish_sub")}
              >
                <Toggle
                  testid="setting-embellish-toggle"
                  checked={!!s.embellish}
                  onChange={() => update("embellish", !s.embellish)}
                />
              </SettingRow>
            </Card>

            <Card>
              <NavRow
                icon={<HelpCircle size={20} />}
                title={t("set_how")}
                subtitle={t("set_how_sub")}
                onClick={() => setView("instructions")}
                testid="nav-instructions"
              />
            </Card>

            <Card>
              <SettingRow icon={<Database size={20} />} title={t("set_data")} subtitle={t("set_data_sub")} />
              <button
                data-testid="data-export-btn"
                onClick={handleExport}
                disabled={busy === "export"}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl bg-[var(--rmr-chip)] py-2.5 text-sm font-semibold text-[var(--rmr-accent)] transition-transform active:scale-[0.98] disabled:opacity-60"
              >
                {busy === "export" ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                {t("set_export")}
              </button>

              {!confirmDelete ? (
                <button
                  data-testid="data-delete-btn"
                  onClick={() => {
                    setNotice("");
                    setConfirmDelete(true);
                  }}
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-2xl bg-[var(--rmr-danger-bg)] py-2.5 text-sm font-semibold text-[var(--rmr-danger)] transition-transform active:scale-[0.98]"
                >
                  <Trash2 size={16} /> {t("set_delete_data")}
                </button>
              ) : (
                <div className="mt-3 rounded-2xl border border-[var(--rmr-danger-border)] bg-[var(--rmr-danger-bg)] p-3">
                  <p className="mb-3 text-center text-sm font-medium text-[var(--rmr-danger)]" data-testid="delete-confirm-text">
                    {t("set_delete_all_text")}
                  </p>
                  <div className="flex gap-2">
                    <button
                      data-testid="delete-cancel"
                      onClick={() => setConfirmDelete(false)}
                      className="flex-1 rounded-2xl bg-[var(--rmr-surface)] py-2.5 text-sm font-semibold text-[var(--rmr-text-2)]"
                    >
                      {t("exp_cancel")}
                    </button>
                    <button
                      data-testid="delete-confirm"
                      onClick={handleDelete}
                      disabled={busy === "delete"}
                      className="flex flex-1 items-center justify-center gap-1 rounded-2xl bg-[var(--rmr-danger)] py-2.5 text-sm font-semibold text-white disabled:opacity-60"
                    >
                      {busy === "delete" ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                      {t("set_delete_yes")}
                    </button>
                  </div>
                </div>
              )}
            </Card>

            <Card>
              <NavRow
                icon={<Shield size={20} />}
                title={t("set_privacy")}
                subtitle={t("set_privacy_sub")}
                onClick={() => { window.location.href = "/confidentialite"; }}
                testid="nav-privacy"
              />
            </Card>

            <Card>
              <SettingRow icon={<User size={20} />} title={t("set_account")} subtitle="">
                <span />
              </SettingRow>
              <p className="mt-2 truncate text-sm font-medium text-[var(--rmr-text)]" data-testid="account-email">
                {userEmail || "…"}
              </p>
              <p className="mt-2 text-xs leading-relaxed text-[var(--rmr-text-3)]">{t("set_account_note")}</p>
              {!BYPASS_AUTH && (
                <button
                  data-testid="logout-btn"
                  onClick={signOut}
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl bg-[var(--rmr-chip)] py-2.5 text-sm font-semibold text-[var(--rmr-text)] transition-transform active:scale-[0.98]"
                >
                  <LogOut size={16} /> {t("set_logout")}
                </button>
              )}
            </Card>

            <ImproveSection t={t} />

            <p className="px-2 pb-1 pt-4 text-xs font-semibold uppercase tracking-wide text-[var(--rmr-text-3)]">
              {t("set_soon")}
            </p>
            <Card>
              <SoonRow icon={<ScanFace size={20} />} title={t("set_face_id")} pill={t("set_soon_pill")} testid="soon-faceid" />
              <Divider />
              <SoonRow icon={<Bell size={20} />} title={t("set_reminders")} pill={t("set_soon_pill")} testid="soon-reminders" />
              <Divider />
              <SoonRow icon={<EyeOff size={20} />} title={t("set_sensitive")} pill={t("set_soon_pill")} testid="soon-sensitive" />
              <Divider />
              <SoonRow icon={<RotateCcw size={20} />} title={t("set_reset_zero")} pill={t("set_soon_pill")} testid="soon-reset" />
              <Divider />
              <SoonRow icon={<MessageSquare size={20} />} title={t("set_feedback")} pill={t("set_soon_pill")} testid="soon-feedback" />
            </Card>

            <p className="pt-2 text-center text-xs text-[var(--rmr-text-3)]">{t("set_footer")}</p>
          </>
        )}
      </div>
    </div>
  );
}

function Card({ children }) {
  return <div className="rounded-3xl border border-[var(--rmr-border)] bg-[var(--rmr-surface)] p-4">{children}</div>;
}
function Divider() {
  return <div className="my-3 h-px bg-[var(--rmr-border)]" />;
}
function IconBadge({ children }) {
  return (
    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-[var(--rmr-chip)] text-[var(--rmr-accent)]">
      {children}
    </span>
  );
}
function SettingRow({ icon, title, subtitle, children }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-3">
        <IconBadge>{icon}</IconBadge>
        <div className="min-w-0">
          <p className="text-[15px] font-semibold text-[var(--rmr-text)]">{title}</p>
          {subtitle && <p className="text-xs text-[var(--rmr-text-2)]">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}
function NavRow({ icon, title, subtitle, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="flex w-full items-center justify-between gap-3 text-left">
      <div className="flex min-w-0 items-center gap-3">
        <IconBadge>{icon}</IconBadge>
        <div className="min-w-0">
          <p className="text-[15px] font-semibold text-[var(--rmr-text)]">{title}</p>
          {subtitle && <p className="text-xs text-[var(--rmr-text-2)]">{subtitle}</p>}
        </div>
      </div>
      <ChevronRight size={20} className="shrink-0 text-[var(--rmr-text-3)]" />
    </button>
  );
}
function SoonRow({ icon, title, pill, testid }) {
  return (
    <div className="flex cursor-not-allowed items-center justify-between gap-3 opacity-50" aria-disabled="true" data-testid={testid}>
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-[var(--rmr-chip)] text-[var(--rmr-text-2)]">
          {icon}
        </span>
        <p className="text-[15px] font-semibold text-[var(--rmr-text)]">{title}</p>
      </div>
      <span className="rounded-full bg-[var(--rmr-chip)] px-2.5 py-1 text-[11px] font-semibold text-[var(--rmr-text-2)]">
        {pill}
      </span>
    </div>
  );
}
function Toggle({ checked, onChange, testid }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      data-testid={testid}
      onClick={onChange}
      className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${
        checked ? "bg-[var(--rmr-accent)]" : "bg-[var(--rmr-border)]"
      }`}
    >
      <span
        className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${
          checked ? "left-6" : "left-1"
        }`}
      />
    </button>
  );
}
function Select({ value, onChange, options, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="shrink-0 rounded-xl border border-[var(--rmr-border)] bg-[var(--rmr-chip)] px-3 py-2 text-sm font-medium text-[var(--rmr-text)] outline-none"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
function Segmented({ value, onChange, options, testid }) {
  return (
    <div className="flex gap-1 rounded-2xl bg-[var(--rmr-chip)] p-1" data-testid={testid}>
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            data-testid={`${testid}-${o.value}`}
            className={`flex-1 rounded-xl py-2 text-sm font-semibold transition-colors ${
              active ? "bg-[var(--rmr-surface)] text-[var(--rmr-accent)] shadow-sm" : "text-[var(--rmr-text-2)]"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Instructions({ t }) {
  const steps = [
    { title: t("instr_1_title"), body: t("instr_1_body") },
    { title: t("instr_2_title"), body: t("instr_2_body") },
    { title: t("instr_3_title"), body: t("instr_3_body") },
  ];
  return (
    <div data-testid="instructions-view" className="space-y-3">
      {steps.map((st, i) => (
        <Card key={i}>
          <div className="flex items-start gap-3">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[var(--rmr-accent)] text-sm font-bold text-white">
              {i + 1}
            </span>
            <div>
              <p className="text-[15px] font-semibold text-[var(--rmr-text)]">{st.title}</p>
              <p className="mt-1 text-sm leading-relaxed text-[var(--rmr-text-2)]">{st.body}</p>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

function Privacy({ t, onData }) {
  const blocks = [
    { title: t("priv_1_title"), body: t("priv_1_body") },
    { title: t("priv_2_title"), body: t("priv_2_body") },
    { title: t("priv_3_title"), body: t("priv_3_body") },
  ];
  return (
    <div data-testid="privacy-view" className="space-y-3">
      {blocks.map((b, i) => (
        <Card key={i}>
          <p className="text-[15px] font-semibold text-[var(--rmr-text)]">{b.title}</p>
          <p className="mt-1 text-sm leading-relaxed text-[var(--rmr-text-2)]">{b.body}</p>
          {i === blocks.length - 1 && (
            <button
              data-testid="privacy-go-data"
              onClick={onData}
              className="mt-3 w-full rounded-2xl bg-[var(--rmr-chip)] py-2.5 text-sm font-semibold text-[var(--rmr-accent)]"
            >
              {t("priv_go_data")}
            </button>
          )}
        </Card>
      ))}
    </div>
  );
}

const KEEP_OPTIONS = [
  ["journal", "imp_keep_journal"],
  ["photos", "imp_keep_photos"],
  ["notes", "imp_keep_notes"],
  ["social", "imp_keep_social"],
  ["none", "imp_keep_none"],
];

function ImproveSection({ t }) {
  const [profile, setProfile] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [survey, setSurvey] = useState({});
  const [contactOk, setContactOk] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getProfile()
      .then((p) => {
        if (!p) return;
        setProfile(p);
        setFirstName(p.first_name || "");
        setBirthYear(p.birth_year ? String(p.birth_year) : "");
        setSurvey(p.survey || {});
        setContactOk(!!p.contact_ok);
      })
      .catch(() => {});
  }, []);

  const flashSaved = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 1600);
  };

  const save = async (patch) => {
    try {
      const updated = await upsertProfile(patch);
      setProfile(updated);
      flashSaved();
    } catch (e) {
      /* silencieux : section facultative */
    }
  };

  const saveSurvey = (next) => {
    setSurvey(next);
    save({ survey: next });
  };

  const toggleKeep = (key) => {
    const arr = Array.isArray(survey.keeps_memories) ? survey.keeps_memories : [];
    const next = arr.includes(key) ? arr.filter((x) => x !== key) : [...arr, key];
    saveSurvey({ ...survey, keeps_memories: next });
  };

  const keeps = Array.isArray(survey.keeps_memories) ? survey.keeps_memories : [];

  return (
    <Card>
      <SettingRow icon={<Lightbulb size={20} />} title={t("imp_title")} subtitle="" />
      <p className="mt-2 text-xs leading-relaxed text-[var(--rmr-text-3)]">{t("imp_header")}</p>
      {saved && (
        <p className="mt-2 text-xs font-semibold text-[var(--rmr-accent)]" data-testid="improve-saved">
          {t("imp_saved")}
        </p>
      )}

      <div className="mt-4 space-y-4" data-testid="improve-section">
        <div>
          <label className="text-sm font-semibold text-[var(--rmr-text)]">{t("imp_firstname")}</label>
          <input
            data-testid="improve-firstname"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            onBlur={() => firstName.trim() !== (profile?.first_name || "") && save({ first_name: firstName.trim() })}
            className="mt-1.5 w-full rounded-2xl border border-[var(--rmr-border)] bg-[var(--rmr-chip)] px-4 py-2.5 text-[15px] text-[var(--rmr-text)] outline-none focus:border-[var(--rmr-accent)]"
          />
        </div>

        <div>
          <label className="text-sm font-semibold text-[var(--rmr-text)]">{t("imp_birthyear")}</label>
          <input
            data-testid="improve-birthyear"
            value={birthYear}
            inputMode="numeric"
            onChange={(e) => setBirthYear(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
            onBlur={() => {
              const y = parseInt(birthYear, 10);
              const val = Number.isFinite(y) ? y : null;
              if (val !== (profile?.birth_year ?? null)) save({ birth_year: val });
            }}
            className="mt-1.5 w-full rounded-2xl border border-[var(--rmr-border)] bg-[var(--rmr-chip)] px-4 py-2.5 text-[15px] text-[var(--rmr-text)] outline-none focus:border-[var(--rmr-accent)]"
          />
        </div>

        <div>
          <label className="text-sm font-semibold text-[var(--rmr-text)]">{t("imp_keeps")}</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {KEEP_OPTIONS.map(([key, label]) => {
              const active = keeps.includes(key);
              return (
                <button
                  key={key}
                  data-testid={`improve-keep-${key}`}
                  onClick={() => toggleKeep(key)}
                  className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                    active ? "bg-[var(--rmr-accent)] text-white" : "bg-[var(--rmr-chip)] text-[var(--rmr-text-2)]"
                  }`}
                >
                  {t(label)}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="text-sm font-semibold text-[var(--rmr-text)]">{t("imp_wants")}</label>
          <textarea
            data-testid="improve-wants"
            value={survey.wants_to_capture || ""}
            onChange={(e) => setSurvey((s) => ({ ...s, wants_to_capture: e.target.value }))}
            onBlur={() => save({ survey: { ...survey } })}
            placeholder={t("imp_wants_ph")}
            rows={2}
            className="mt-1.5 w-full resize-none rounded-2xl border border-[var(--rmr-border)] bg-[var(--rmr-chip)] px-4 py-2.5 text-[15px] text-[var(--rmr-text)] outline-none focus:border-[var(--rmr-accent)]"
          />
        </div>

        <div>
          <label className="text-sm font-semibold text-[var(--rmr-text)]">{t("imp_heard")}</label>
          <div className="mt-2">
            <Select
              testid="improve-heard"
              value={survey.heard_from || ""}
              onChange={(v) => saveSurvey({ ...survey, heard_from: v })}
              options={[
                { value: "", label: "—" },
                { value: "friend", label: t("imp_heard_friend") },
                { value: "eric", label: t("imp_heard_eric") },
                { value: "other", label: t("imp_heard_other") },
              ]}
            />
          </div>
        </div>

        <div>
          <label className="text-sm font-semibold text-[var(--rmr-text)]">{t("imp_tech")}</label>
          <div className="mt-2">
            <Segmented
              testid="improve-tech"
              value={survey.tech_comfort || ""}
              onChange={(v) => saveSurvey({ ...survey, tech_comfort: v })}
              options={[
                { value: "low", label: t("imp_tech_low") },
                { value: "mid", label: t("imp_tech_mid") },
                { value: "high", label: t("imp_tech_high") },
              ]}
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-medium text-[var(--rmr-text-2)]">{t("imp_contact")}</p>
          <Toggle
            testid="improve-contact-toggle"
            checked={contactOk}
            onChange={() => {
              const next = !contactOk;
              setContactOk(next);
              save({ contact_ok: next });
            }}
          />
        </div>
      </div>
    </Card>
  );
}

